import React from 'react';
import { motion } from 'framer-motion';

function About() {
  const stats = [
    { number: '500+', label: 'High-Net-Worth Clients', description: 'Trusted by discerning investors worldwide' },
    { number: '$5.2B+', label: 'Assets Under Management', description: 'Comprehensive wealth management solutions' },
    { number: '35+', label: 'Global Markets', description: 'Extensive international reach and expertise' },
    { number: '72%', label: 'Client Retention Rate', description: 'Proven track record of client satisfaction' },
  ];

  const values = [
    {
      title: 'Our Approach',
      description: 'We help sovereign institutions navigate shifting dynamics that impact both their existing asset allocations and long-term investment strategies. Our global network and longstanding relationships allow us to identify relevant market trends and provide thoughtful, timely and innovative solutions.',
      image: '/new-assets/pexels-alphatradezone-7352527.jpg'
    },
    {
      title: 'Investment Philosophy',
      description: 'We follow a disciplined investment approach built on timeless principles and cutting-edge research. Our focus on long-term wealth creation rather than short-term market fluctuations allows us to capitalize on sustainable growth opportunities while protecting capital during market downturns.',
      image: '/new-assets/pexels-edmond-dantes-4342494.jpg'
    },
    {
      title: 'Client-Centric Focus',
      description: 'Our relationship promise to all our clients is that you will deal directly with decision makers. We don\'t just look at your assets or security. Our experienced relationship managers have the expertise to see and understand your unique opportunities and potential.',
      image: '/new-assets/pexels-jakubzerdzicki-20801620.jpg'
    }
  ];

  const principles = [
    {
      number: '01',
      title: 'Long-Term Perspective',
      description: 'We focus on long-term wealth creation rather than short-term market fluctuations, allowing us to capitalize on sustainable growth opportunities.'
    },
    {
      number: '02', 
      title: 'Risk Management',
      description: 'Our rigorous risk management framework aims to protect capital during market downturns while capturing upside potential during favorable conditions.'
    },
    {
      number: '03',
      title: 'Diversification',
      description: 'We believe in strategic diversification across asset classes, geographies, and investment styles to reduce portfolio volatility and enhance returns.'
    },
    {
      number: '04',
      title: 'Research-Driven Approach',
      description: 'Our investment decisions are based on deep fundamental research, quantitative analysis, and a thorough understanding of global markets.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img 
          src="/new-assets/pexels-vlada-karpovich-7433850.jpg"
          alt="Blockfirst Capital Team"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.02em] mb-6">
              <span className="block font-light">About</span>
              <span className="block font-medium text-[#fbbf24]">Blockfirst Capital</span>
            </h1>
            <p className="text-white/90 text-xl sm:text-2xl font-light leading-relaxed max-w-3xl mx-auto">
              Pioneering wealth management solutions for the modern investor
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Trusted by Leaders</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our track record speaks to our commitment to excellence and client success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-4xl font-bold text-[#0056d7] mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`flex flex-col lg:flex-row items-center gap-12 mb-20 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className="flex-1">
                <h3 className="text-3xl font-light text-gray-900 mb-6">{value.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{value.description}</p>
              </div>
              <div className="flex-1">
                <img 
                  src={value.image}
                  alt={value.title}
                  className="w-full h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Investment Philosophy */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Our Investment Philosophy</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on timeless principles and cutting-edge research
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="absolute -top-4 left-8 w-12 h-12 bg-[#0056d7] text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {principle.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-4">{principle.title}</h3>
                <p className="text-gray-600 leading-relaxed">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4">Meet Our Leadership</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced professionals dedicated to your financial success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Priya Patel',
                title: 'Head of Client Relations',
                image: '/images/people/pexels-tima-miroshnichenko-5336962.jpg',
                description: 'Over 20 years of experience in global wealth management and institutional investment strategies.'
              },
              {
                name: 'David Kim',
                title: 'Chief Investment Officer',
                image: '/images/people/pexels-tima-miroshnichenko-5439443 (1).jpg',
                description: 'Leading investment strategist with expertise in portfolio optimization and risk management.'
              },
              {
                name: 'Ethan Carter',
                title: 'Chief Executive Officer',
                image: '/images/people/pexels-yankrukov-7793146.jpg',
                description: 'Dedicated to building lasting relationships and delivering exceptional client experiences.'
              }
            ].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow duration-300"
              >
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 object-cover rounded-full mx-auto mb-6 shadow-lg"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-[#0056d7] font-medium mb-4">{member.title}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[#0056d7] to-[#003a9e]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-light text-white mb-6">Ready to Begin Your Journey?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Join hundreds of clients who trust us with their wealth management needs
            </p>
            <motion.a
              href="/contact"
              className="inline-block px-12 py-6 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium text-xl transition-all duration-500 shadow-2xl hover:shadow-amber-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Consultation
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;
